import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, cn, MathText, Paginator } from '@jeraisonne/ui';
import { ArrowLeft, ChevronRight, Clock, Pencil } from 'lucide-react';
import type { SchoolLevel } from '@jeraisonne/db';
import {
  OBJECTIVE_RULE_LABELS,
  objectiveCardsLabel,
  objectiveNeedsPack,
} from '@jeraisonne/db/objectives';
import { formatHora } from '@jeraisonne/db/cards';
import type {
  AppStudentObjective,
  ObjectiveProgress,
} from '@/lib/student-objectives/objective-types';
import { supabase } from '@/lib/supabase';
import { updateObjective } from '@/lib/student-objectives/student-objectives';
import { objectiveScopeKey, type ObjectiveScope } from '@/lib/student-objectives/objective-scope';
import {
  computeDeadlineCountdown,
  getDeadlineBadgeClasses,
} from '@/lib/student-objectives/deadline-format';
import { usePagedList } from '@/lib/use-paged-list';
import { SkillcardRow } from '@/features/skillcards/skillcard-row';
import { ObjectiveProgressRing } from './objective-progress-ring';
import { ObjectivePackPicker } from './objective-pack-picker';
import { DeadlineEditor } from './deadline-editor';
import { DiagnosticBreakdownPanel } from './diagnostic-breakdown-panel';
import { Spinner } from '@/components/spinner';

export interface ExerciseWithAccess {
  id: string;
  title: string;
  difficulty: 'easy' | 'medium' | 'hard';
  estimatedMinutes?: number | null;
  /** Contexte affiché sous le titre (SKILLCARD reliée, ou chapitre). */
  subtitle?: string;
  illustrationUrl?: string | null;
  inProgress?: boolean;
  assignment?: {
    source: 'tutor' | 'class';
    label: string;
  };
}

const DIFFICULTY_META = {
  easy: { label: 'Facile', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  medium: { label: 'Moyen', cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  hard: { label: 'Difficile', cls: 'text-red-700 bg-red-50 border-red-200' },
} as const;

/** Nombre de SKILLCARDS affichées avant « Voir toutes ». */
const CARDS_PREVIEW = 5;

interface Props {
  objective: AppStudentObjective;
  /** Périmètre SKILLCARDS (migration 0514) ; null pour l'ancien diagnostic. */
  scope: ObjectiveScope | null;
  studentLevel: SchoolLevel | null;
  progress: ObjectiveProgress | null;
  exercises: ExerciseWithAccess[];
  isLoadingExercises: boolean;
  onBack: () => void;
  onStartExercise: (id: string) => void;
  /** Exercice en cours d'ouverture (spinner sur son bouton). */
  startingExerciseId?: string | null;
}

export function ObjectiveDetailView({
  objective,
  scope,
  studentLevel,
  progress,
  exercises,
  isLoadingExercises,
  onBack,
  onStartExercise,
  startingExerciseId = null,
}: Props) {
  const cp = progress?.completionPercent ?? 0;
  const mp = progress?.masteryPercent ?? 0;

  const illustrationUrl = objective.template.illustrationUrl ?? null;
  const hasIllustration = !!illustrationUrl;
  const countdown = computeDeadlineCountdown(objective.deadline);
  const needsPack = scope
    ? objectiveNeedsPack({ rule: scope.rule, selectedPackId: objective.selectedPackId })
    : false;
  const packSubtitle = scope && !needsPack ? scope.packs.map((p) => p.title).join(' · ') : '';
  const paged = usePagedList(exercises, {
    unit: 'exercices',
    resetKeys: [objective.selectedPackId],
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl',
          hasIllustration && 'sm:min-h-88 min-h-72',
        )}
      >
        {hasIllustration && (
          <>
            <img
              src={illustrationUrl!}
              alt=""
              loading="lazy"
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            />
            <div
              aria-hidden="true"
              className="bg-linear-to-b via-ink-50/60 to-ink-50/95 pointer-events-none absolute inset-0 from-transparent"
            />
          </>
        )}
        <button
          type="button"
          onClick={onBack}
          aria-label="Retour au dashboard"
          className={cn(
            'absolute left-3 top-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-sm transition-colors active:scale-95',
            hasIllustration
              ? 'border-ink-100 text-ink-700 bg-white/85 backdrop-blur hover:bg-white'
              : 'border-ink-200 text-ink-700 hover:bg-ink-50 bg-white',
          )}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        {countdown && (
          <span
            className={cn(
              'absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-sans text-[12px] font-semibold tabular-nums',
              getDeadlineBadgeClasses(countdown.urgency),
            )}
            aria-label={`Date butoir : ${countdown.formatted}`}
          >
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {countdown.formatted}
          </span>
        )}
        <div
          className={cn(
            'flex items-start gap-4',
            hasIllustration ? 'absolute inset-x-0 bottom-0 p-4' : 'relative pt-16',
          )}
        >
          <ObjectiveProgressRing completionPercent={cp} masteryPercent={mp} size="lg" />
          <div className="min-w-0 flex-1">
            <h1 className="text-ink-900 text-xl font-bold leading-tight">
              {objective.template.title}
            </h1>
            {packSubtitle && (
              <p className="text-primary-700 mt-0.5 text-sm font-semibold">{packSubtitle}</p>
            )}
            <p className="text-ink-500 mt-0.5 text-sm">{objective.template.description}</p>
            {progress?.kind === 'cards' ? (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="text-primary-600 font-medium">
                  {objectiveCardsLabel(progress.cards)}
                </span>
                <span className="text-accent-600 font-medium">{mp}% maîtrise</span>
                {(progress.cards?.hora ?? 0) > 0 && (
                  <span className="text-ink-500">{formatHora(progress.cards!.hora)}</span>
                )}
              </div>
            ) : progress ? (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="text-primary-600 font-medium">{cp}% complétés</span>
                <span className="text-accent-600 font-medium">{mp}% maîtrise</span>
                <span className="text-ink-400">
                  {progress.completedExercises}/{progress.totalExercises} exercices
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Deadline */}
      <DeadlineEditor objective={objective} />

      {/* Pack : choisi par l'élève, ou pack du moment (automatique). */}
      {scope?.rule === 'chosen_pack' && (
        <PackChoice objective={objective} scope={scope} needsPack={needsPack} />
      )}
      {scope && scope.rule !== 'chosen_pack' && scope.rule !== 'pinned' && (
        <p className="text-ink-500 border-ink-100 rounded-2xl border bg-white/70 px-4 py-3 text-sm">
          {OBJECTIVE_RULE_LABELS[scope.rule]}
          {scope.rule === 'current_pack' && packSubtitle ? ` : ${packSubtitle}` : ''}
        </p>
      )}

      {/* SKILLCARDS du périmètre */}
      {scope && !needsPack && <ObjectiveCards scope={scope} />}

      {/* Bilan par chapitre — uniquement pour l'ancien objectif Diagnostic */}
      {objective.template.type === 'diagnostic' && studentLevel && (
        <DiagnosticBreakdownPanel
          studentId={objective.studentId}
          studentLevel={studentLevel}
          onStartExercise={onStartExercise}
          startingExerciseId={startingExerciseId}
        />
      )}

      {/* Exercises */}
      {!needsPack && (
        <div className="space-y-3">
          <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
            Exercices ({exercises.length})
          </p>

          {isLoadingExercises ? (
            <div className="animate-pulse space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-ink-100 h-16 rounded-xl" />
              ))}
            </div>
          ) : exercises.length === 0 ? (
            <Card className="border-ink-100 border-dashed">
              <CardContent className="pb-6 pt-6 text-center">
                <p className="text-ink-400 text-sm">Aucun exercice trouvé pour cet objectif.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {paged.items.map((ex) => (
                <ExerciseRow
                  key={ex.id}
                  exercise={ex}
                  onStart={() => onStartExercise(ex.id)}
                  isStarting={startingExerciseId === ex.id}
                  disabled={startingExerciseId !== null}
                />
              ))}
              <Paginator {...paged.paginator} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Pack de l'objectif « Réviser un pack » : choix, ou rappel modifiable. */
function PackChoice({
  objective,
  scope,
  needsPack,
}: {
  objective: AppStudentObjective;
  scope: ObjectiveScope;
  needsPack: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pack = scope.packs[0] ?? null;

  async function choose(packId: string) {
    setSaving(true);
    setError(null);
    try {
      await updateObjective(supabase, objective.id, { selectedPackId: packId });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['student-objectives'] }),
        queryClient.invalidateQueries({ queryKey: objectiveScopeKey(objective.id) }),
        queryClient.invalidateQueries({ queryKey: ['objectives-progress'] }),
      ]);
      setEditing(false);
    } catch {
      setError('Le pack n’a pas pu être enregistré. Réessaie.');
    } finally {
      setSaving(false);
    }
  }

  if (needsPack || editing || !pack) {
    return (
      <section className="space-y-3">
        <div>
          <h2 className="text-ink-900 text-base font-bold">Choisis ton pack</h2>
          <p className="text-ink-500 text-sm">
            Ses SKILLCARDS et leurs exercices formeront ton objectif.
          </p>
        </div>
        <ObjectivePackPicker
          selectedPackId={objective.selectedPackId}
          onSelect={(p) => void choose(p.id)}
          disabled={saving}
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!needsPack && pack && (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-ink-500 hover:text-ink-700 min-h-[2.75rem] w-full text-center text-[13px] underline-offset-2 hover:underline"
          >
            Annuler
          </button>
        )}
      </section>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="border-ink-100 hover:border-primary-300 hover:bg-primary-50/40 flex w-full items-center gap-3 rounded-2xl border bg-white/70 p-3 text-left backdrop-blur-sm transition-colors active:scale-[0.99]"
    >
      <div className="min-w-0 flex-1">
        <p className="font-display text-ink-500 text-[11px] font-semibold uppercase tracking-wider">
          Pack
        </p>
        <p className="font-display text-ink-900 mt-0.5 truncate text-[15px] font-semibold">
          {pack.title}
        </p>
      </div>
      <span
        aria-label="Changer de pack"
        className="border-ink-200 text-ink-500 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border bg-white"
      >
        <Pencil className="h-4 w-4" />
      </span>
    </button>
  );
}

/** SKILLCARDS de l'objectif : les moins maîtrisées d'abord. */
function ObjectiveCards({ scope }: { scope: ObjectiveScope }) {
  const [showAll, setShowAll] = useState(false);
  if (scope.cards.length === 0) {
    return (
      <p className="text-ink-500 rounded-2xl border border-dashed p-4 text-center text-sm">
        Aucune SKILLCARD dans cet objectif pour l’instant.
      </p>
    );
  }
  const visible = showAll ? scope.cards : scope.cards.slice(0, CARDS_PREVIEW);
  return (
    <section className="space-y-2">
      <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
        SKILLCARDS ({scope.cards.length})
      </p>
      {visible.map((c) => (
        <SkillcardRow key={c.id} card={c} />
      ))}
      {scope.cards.length > CARDS_PREVIEW && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="text-primary-700 min-h-[2.75rem] w-full rounded-2xl text-sm font-semibold active:scale-[0.99]"
        >
          {showAll ? 'Voir moins' : `Voir les ${scope.cards.length} SKILLCARDS`}
        </button>
      )}
    </section>
  );
}

function ExerciseRow({
  exercise,
  onStart,
  isStarting = false,
  disabled = false,
}: {
  exercise: ExerciseWithAccess;
  onStart: () => void;
  isStarting?: boolean;
  disabled?: boolean;
}) {
  const diff = DIFFICULTY_META[exercise.difficulty];
  const hasIllustration = !!exercise.illustrationUrl;

  return (
    <button
      type="button"
      onClick={onStart}
      disabled={disabled}
      aria-busy={isStarting || undefined}
      className="w-full text-left active:scale-[0.98]"
    >
      <Card
        className={cn(
          'border-ink-100 hover:border-primary-200 relative overflow-hidden transition-colors',
          hasIllustration && 'min-h-40',
        )}
      >
        {hasIllustration && (
          <>
            <img
              src={exercise.illustrationUrl!}
              alt=""
              loading="lazy"
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-2/3 w-full object-cover"
            />
            <div
              aria-hidden="true"
              className="bg-linear-to-t from-ink-50 via-ink-50/85 pointer-events-none absolute inset-0 to-transparent"
            />
          </>
        )}
        <CardContent
          className={cn('relative py-3', hasIllustration && 'absolute inset-x-0 bottom-0')}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-ink-900 truncate text-sm font-semibold">
                <MathText>{exercise.title}</MathText>
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                {exercise.inProgress && (
                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">
                    En cours
                  </span>
                )}
                {exercise.assignment && (
                  <span
                    className={cn(
                      'rounded-full border px-1.5 py-0.5 text-xs font-medium',
                      exercise.assignment.source === 'tutor'
                        ? 'border-violet-200 bg-violet-50 text-violet-700'
                        : 'border-blue-200 bg-blue-50 text-blue-700',
                    )}
                  >
                    {exercise.assignment.label}
                  </span>
                )}
                <span
                  className={cn('rounded-full border px-1.5 py-0.5 text-xs font-medium', diff.cls)}
                >
                  {diff.label}
                </span>
                {exercise.subtitle && (
                  <span className="text-ink-400 truncate text-xs">
                    <MathText>{exercise.subtitle}</MathText>
                  </span>
                )}
                {exercise.estimatedMinutes ? (
                  <span className="text-ink-300 text-xs">~{exercise.estimatedMinutes} min</span>
                ) : null}
              </div>
            </div>
            <div
              className={cn(
                'flex h-9 shrink-0 items-center gap-1 rounded-xl px-3 text-xs font-bold text-white',
                exercise.inProgress ? 'bg-amber-400' : 'bg-primary-400',
              )}
            >
              {isStarting ? (
                <>
                  <Spinner />
                  Ouverture…
                </>
              ) : (
                <>
                  {exercise.inProgress ? 'Reprendre' : 'Lancer'}
                  <ChevronRight className="h-3.5 w-3.5" />
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </button>
  );
}
